import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { PatientHistoryEntry, useAdvancedPatientHistoryState } from '../../../api/useAdvancedPatientHistory'
import { usePanelPatientUid } from '../../../dashboard/panelContext'
import { isDeletable } from '../../../api/useDeletePatientHistoryEntry'
import { formatUsDate, formatUsDateTime, toIso } from '../../../format'
import { advancedColors } from '../../../theme'
import { EXAMPLE_PROVIDER } from '../../../tour/exampleFeed'
import { ExampleTag } from '../../../tour/examples'
import { useTourExample } from '../../../tour/tourExample'
import { exampleHistoryEntries } from '../../../tour/exampleHistory'
import { EntryFiles, valueOf } from '../EntryBody'
import { renderBodyForType } from '../renderBodyForType'
import { labelForHistoryType } from '../typeLabels'
import DeleteEntryButton from './DeleteEntryButton'
import SubmittedBy from './SubmittedBy'

const EventCard = ({
  uid,
  entry,
  expanded,
  onToggle,
  example = false,
}: {
  uid: string
  entry: PatientHistoryEntry
  expanded: boolean
  onToggle: () => void
  example?: boolean
}) => {
  const label = labelForHistoryType(entry.type, valueOf(entry))
  const createdAt = toIso(entry.createdAt)

  return (
    <Box
      id={`patient-history-${entry.entryID}`}
      sx={{ border: `1px solid ${advancedColors.line}`, borderRadius: 2, backgroundColor: advancedColors.surface }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Box
          component="button"
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            p: 1.5,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: advancedColors.ink }}>
                {label}
              </Typography>
              {example && <ExampleTag />}
            </Box>
            <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
              Date of event: {formatUsDate(entry.date ?? undefined)}
            </Typography>
            <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
              Submitted{' '}
              {example ? (
                <>by {EXAMPLE_PROVIDER.name}</>
              ) : (
                <SubmittedBy coachID={entry.coachID} source={entry.source} />
              )}
              {createdAt && ` on ${formatUsDateTime(createdAt)}`}
            </Typography>
          </Box>
          <ExpandMoreRoundedIcon
            sx={{
              color: advancedColors.inkMuted,
              transition: 'transform 120ms ease-in-out',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
        {!example && createdAt && isDeletable({ createdAt, source: entry.source }) && (
          <DeleteEntryButton uid={uid} entryID={entry.entryID} label={label} />
        )}
      </Box>

      {expanded && (
        <Box sx={{ px: 1.5, pb: 1.5, borderTop: `1px solid ${advancedColors.line}`, pt: 1.5 }}>
          {renderBodyForType(entry)}
          <EntryFiles files={entry.files} />
        </Box>
      )}
    </Box>
  )
}

const EventHistory = () => {
  const uid = usePanelPatientUid()
  const { entries, loading, failed } = useAdvancedPatientHistoryState(uid)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // A failed load says nothing about the log, so it never gets examples.
  const showsExample = useTourExample() === 'event-log' && entries.length === 0 && !loading && !failed
  const exampleEntries = useMemo(exampleHistoryEntries, [])
  const shown = showsExample ? exampleEntries : entries

  if (loading) {
    return null
  }

  if (shown.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
        No events recorded yet.
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {shown.map((entry) => (
        <EventCard
          key={entry.entryID}
          uid={uid}
          entry={entry}
          example={showsExample}
          expanded={expandedId === entry.entryID}
          onToggle={() => setExpandedId((current) => (current === entry.entryID ? null : entry.entryID))}
        />
      ))}
    </Box>
  )
}

export default EventHistory
